if (!customElements.get('store-availability')) {
  class StoreAvailability extends HTMLElement {
    constructor() {
      super();
      this.ready = false;
      this.onVariantChange = this.handleVariantChange.bind(this);
      window.initLazyScript(this, this.init.bind(this));
    }

    // Listens from the start: init() is lazy and the block sits in a closed
    // disclosure, so a variant switch before it ran left the old rows on screen.
    connectedCallback() {
      document.addEventListener('on:variant:change', this.onVariantChange);
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this.onVariantChange);
    }

    init() {
      if (this.ready) return;
      this.ready = true;
      this.body = this.querySelector('.js-store-availability-body');
      this.readData();
      this.threshold = parseInt(this.dataset.qtyThreshold, 10) || 0;
      this.iconAvailable = this.querySelector('.js-store-availability-icon-available')?.innerHTML || '';
      this.iconUnavailable = this.querySelector('.js-store-availability-icon-unavailable')?.innerHTML || '';

      this.productInfo = this.closest('.product-info');

      // Render the currently selected variant — it may differ from the
      // server-rendered default if the variant changed before this lazy init.
      const selectedId = this.getSelectedVariantId();
      if (selectedId && this.variants[selectedId]) {
        this.render(this.variants[selectedId]);
      }
    }

    readData() {
      const data = this.getData();
      this.stores = data.stores || [];
      this.variants = data.variants || {};
    }

    getData() {
      const dataEl = this.querySelector('.js-store-availability-data');
      try {
        return JSON.parse(dataEl.textContent);
      } catch (e) {
        return {};
      }
    }

    getSelectedVariantId() {
      const input = this.productInfo?.querySelector('[name="id"]');
      return input ? input.value : null;
    }

    /**
     * Handles a 'on:variant:change' event from the variant picker.
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (!(this.closest('.product-info') || document).contains(evt.target)) return;

      this.init();
      // A product switch swaps the data script for the new product's.
      this.readData();
      const variant = evt.detail && evt.detail.variant;
      this.render(variant ? this.variants[variant.id] : null);
    }

    formatCount(value) {
      return new Intl.NumberFormat('cs-CZ').format(value);
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    /**
     * Re-renders the per-store rows for the given variant.
     * @param {{available: boolean, continueSelling: boolean, qty: number[]}} variantData
     */
    render(variantData) {
      if (!this.body) return;

      const data = variantData || { available: false, continueSelling: false, qty: [] };
      const orderable = !!data.available;
      // "Continue selling when out of stock" only makes the variant orderable (restock rows).
      const continueSelling = !!data.continueSelling;
      const qty = data.qty || [];
      let anyShown = orderable || continueSelling;

      const items = this.stores
        .map((store, i) => {
          const stock = qty[i] || 0;
          let state;
          let value;
          let note = '';

          if (stock > 0) {
            state = 'in_store';
            anyShown = true;
            value = this.threshold > 0 && stock > this.threshold
              ? `${this.dataset.qtyOver}&nbsp;${this.dataset.unit}`
              : `${this.formatCount(stock)}&nbsp;${this.dataset.unit}`;
            if (store.inStore) {
              note = `<span class="product-store-availability__note">${this.escapeHtml(store.inStore)}</span>`;
            }
          } else if (orderable || continueSelling) {
            state = 'restock';
            value = this.escapeHtml(store.restock || this.dataset.unit);
          } else {
            state = 'unavailable';
            value = this.dataset.unavailable;
          }

          const icon = state === 'unavailable' ? this.iconUnavailable : this.iconAvailable;
          return `<li class="product-store-availability__item product-store-availability__item--${state}">${icon}<span class="product-store-availability__name">${this.escapeHtml(store.name)}${note}</span><span class="product-store-availability__value">${value}</span></li>`;
        })
        .join('');

      if (!anyShown) {
        this.body.innerHTML = `<p class="product-store-availability__none">${this.dataset.none}</p>`;
        return;
      }

      this.body.innerHTML = `<ul role="list" class="product-store-availability__list">${items}</ul>`;
    }
  }

  customElements.define('store-availability', StoreAvailability);
}
